import { productTypes } from '../../../utils/productHelpers';

/**
 * @typedef {Object} IsaAllowance
 * @property {string} [taxYear]
 * @property {number} [totalAllowance]
 * @property {number} [usedAllowance]
 * @property {number} [remainingAllowance]
 * @property {number} [holdAllowance]
 */

/**
 * Check if customer has at least one ISA account
 * @param {any[]} accounts - Array of account objects
 * @returns {boolean}
 */
export function hasIsaAccount(accounts = []) {
    return accounts.some((/** @type {any} */ account) => 
        account && 
        account.product && 
        (account.product.productType === productTypes.FixedRateCashISA ||
            account.product.productType === productTypes.InstantAccessCashISA)
    );
}

/**
 * Calculate ISA remaining allowance with fallback logic
 * @param {IsaAllowance | undefined} isaAllowance - ISA allowance data
 * @returns {number}
 */
export function getIsaRemainingAllowance(isaAllowance) {
    // If we have data and remainingAllowance is provided as a number, use it
    if (isaAllowance && typeof isaAllowance.remainingAllowance === 'number') {
        return Math.max(0, Number(isaAllowance.remainingAllowance));
    }
    
    // Otherwise calculate from totalAllowance - usedAllowance (with defaults)
    const safeTotal = Math.max(0, Number(isaAllowance?.totalAllowance) || 20000);
    const safeUsedRaw = Math.max(0, Number(isaAllowance?.usedAllowance) || 0);
    const safeUsed = Math.min(safeUsedRaw, safeTotal);
    return Math.max(0, safeTotal - safeUsed);
}

/**
 * Calculate ISA progress percentage
 * @param {IsaAllowance | undefined} isaAllowance - ISA allowance data
 * @param {number} [extraUsedAllowance=0] - Additional reserved allowance to include in the bar
 * @returns {number}
 */
export function getIsaProgressPercentage(isaAllowance, extraUsedAllowance = 0) {
    if (!isaAllowance) return 0;
    const total = Number(isaAllowance.totalAllowance) || 20000;
    const used = Math.max(0, (Number(isaAllowance.usedAllowance) || 0) + (Number(extraUsedAllowance) || 0));
    const boundedUsed = Math.min(used, total);
    return total > 0 ? (boundedUsed / total) * 100 : 0;
}
